/**
 * Image dimension calculation utilities
 * 
 * Handles calculating resized dimensions based on original aspect ratios
 * without needing to wait for images to load.
 */

/**
 * Calculate dimensions for a resize_to_fill operation (crops to exact size)
 * This matches Active Storage's resize_to_fill behavior
 * 
 * @param {number} originalWidth - Original image width
 * @param {number} originalHeight - Original image height
 * @param {number} targetWidth - Target width
 * @param {number} targetHeight - Target height
 * @returns {Object} { width, height } - Final dimensions after crop
 */
export function calculateResizeToFill(originalWidth, originalHeight, targetWidth, targetHeight) {
  // For resize_to_fill, the result is always exactly the target dimensions
  // (the image is cropped to fit)
  return {
    width: targetWidth,
    height: targetHeight
  };
}

/**
 * Calculate dimensions for a resize_to_limit operation (maintains aspect ratio)
 * This matches Active Storage's resize_to_limit behavior
 * 
 * @param {number} originalWidth - Original image width
 * @param {number} originalHeight - Original image height
 * @param {number} maxWidth - Maximum width
 * @param {number} maxHeight - Maximum height
 * @returns {Object} { width, height } - Final dimensions maintaining aspect ratio
 */
export function calculateResizeToLimit(originalWidth, originalHeight, maxWidth, maxHeight) {
  if (!originalWidth || !originalHeight) {
    return { width: maxWidth, height: maxHeight };
  }

  const aspectRatio = originalWidth / originalHeight;
  
  // Calculate dimensions that fit within the max bounds while maintaining aspect ratio
  let width = originalWidth;
  let height = originalHeight;
  
  // If image is larger than max dimensions, scale it down
  if (width > maxWidth || height > maxHeight) {
    if (width / maxWidth > height / maxHeight) {
      // Width is the limiting factor
      width = maxWidth;
      height = Math.round(width / aspectRatio);
    } else {
      // Height is the limiting factor
      height = maxHeight;
      width = Math.round(height * aspectRatio);
    }
  }
  
  return { width, height };
}

/**
 * Calculate dimensions for rendering at a specific resolution while maintaining aspect ratio
 * This is used for AI renders which maintain the original aspect ratio
 * 
 * @param {number} originalWidth - Original image width
 * @param {number} originalHeight - Original image height
 * @param {number} targetResolution - Target resolution (e.g., 512, 1024)
 * @returns {Object} { width, height } - Rendered dimensions at target resolution
 */
export function calculateRenderDimensions(originalWidth, originalHeight, targetResolution) {
  if (!originalWidth || !originalHeight) {
    return { width: targetResolution, height: targetResolution };
  }

  const aspectRatio = originalWidth / originalHeight;
  
  let width, height;
  
  if (aspectRatio > 1) {
    // Landscape: width is the limiting factor
    width = targetResolution;
    height = Math.round(targetResolution / aspectRatio);
  } else if (aspectRatio < 1) {
    // Portrait: height is the limiting factor
    height = targetResolution;
    width = Math.round(targetResolution * aspectRatio);
  } else {
    // Square
    width = targetResolution;
    height = targetResolution;
  }
  
  return { width, height };
}

/**
 * Calculate how an image with object-fit: contain will be rendered in a container
 * This matches CSS object-fit: contain behavior
 * 
 * @param {number} imageWidth - Image width
 * @param {number} imageHeight - Image height
 * @param {number} containerWidth - Container width
 * @param {number} containerHeight - Container height
 * @returns {Object} { width, height, left, top } - Rendered dimensions and position
 */
export function calculateContainDimensions(imageWidth, imageHeight, containerWidth, containerHeight) {
  if (!imageWidth || !imageHeight) {
    return {
      width: containerWidth,
      height: containerHeight,
      left: 0,
      top: 0
    };
  }

  const imageAspectRatio = imageWidth / imageHeight;
  const containerAspectRatio = containerWidth / containerHeight;
  
  let width, height;
  
  if (imageAspectRatio > containerAspectRatio) {
    // Image is wider than container - width is limiting factor
    width = containerWidth;
    height = width / imageAspectRatio;
  } else {
    // Image is taller than container - height is limiting factor
    height = containerHeight;
    width = height * imageAspectRatio;
  }
  
  // Center the image in the container
  const left = (containerWidth - width) / 2;
  const top = (containerHeight - height) / 2;
  
  return { width, height, left, top };
}

/**
 * Get the appropriate thumbnail dimensions for a photo at a given resolution
 * Handles the current square-crop thumbnails and future aspect-ratio-preserving thumbnails
 * 
 * @param {Object} photo - Photo object with original_width and original_height
 * @param {number} resolution - Desired resolution (256, 512, 768, 1024)
 * @returns {Object} { width, height } - Thumbnail dimensions
 */
export function getThumbnailDimensions(photo, resolution) {
  if (!photo.original_width || !photo.original_height) {
    // Fallback if dimensions not available
    return { width: resolution, height: resolution };
  }

  // Currently using resize_to_fill (square crop)
  // TODO: When we switch to resize_to_limit, use calculateResizeToLimit instead
  return calculateResizeToFill(
    photo.original_width,
    photo.original_height,
    resolution,
    resolution
  );
}

/**
 * Get expected dimensions for a rendered image at a specific resolution
 * 
 * @param {Object} photo - Photo object with original_width and original_height
 * @param {number} resolution - Render resolution (512, 768, 1024)
 * @returns {Object} { width, height } - Expected render dimensions
 */
export function getRenderedDimensions(photo, resolution) {
  if (!photo.original_width || !photo.original_height) {
    // Fallback if dimensions not available
    return { width: resolution, height: resolution };
  }

  return calculateRenderDimensions(
    photo.original_width,
    photo.original_height,
    resolution
  );
}

/**
 * Calculate the bounds of an image rendered with object-fit: contain in a container
 * 
 * @param {Object} photo - Photo object with original_width and original_height
 * @param {number} resolution - Image resolution being displayed
 * @param {HTMLElement} container - Container element
 * @returns {Object} { width, height, left, top } - Rendered bounds
 */
export function calculateImageBounds(photo, resolution, container) {
  if (!photo.original_width || !photo.original_height || !container) {
    return null;
  }

  const renderedDimensions = getRenderedDimensions(photo, resolution);
  const containerRect = container.getBoundingClientRect();
  
  return calculateContainDimensions(
    renderedDimensions.width,
    renderedDimensions.height,
    containerRect.width,
    containerRect.height
  );
}

